import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AlertService } from '@app/_services';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { OwnerStmtService } from '@app/_components/owner-stmt/owner-stmt.service';
import { ReportService } from '@app/_components/report';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ExpenseListComponent } from '@app/_components/expense/expense-list/expense-list.component';
import * as moment from 'moment';
import { PropertyService } from '@app/_components/property';

@Component({
    selector: 'app-owner-statements-reconsiliation',
    templateUrl: './owner-statements-reconsiliation.component.html',
    styleUrls: ['./owner-statements-reconsiliation.component.css'],
    providers: [MessageService],
    standalone: false
})
export class OwnerStatementsReconsiliationComponent implements OnInit {
	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;
	@ViewChild('dt2') dt2: Table;

	form1: UntypedFormGroup;
	reconciliationData: any[] = [];
	shortTermRentals: any[] = [];
	propertyOptions: any[] = [];
	loading = false;
	matchStatusOptions: any[] = [
		{ label: 'All', value: null },
		{ label: 'Matched', value: true },
		{ label: 'Mismatch', value: false }
	];

	columns: any[] = [
		{ field: 'propertyCode', header: 'Property', width: 'auto' },
		{ field: 'startDate', header: 'Start Date', width: 'auto' },
		{ field: 'endDate', header: 'End Date', width: 'auto' },
		{ field: 'expenseAmount', header: 'Expense Amount', width: 'auto' },
		{ field: 'paidByOwner', header: 'Paid By Owner', width: 'auto' },
		{ field: 'paidByKbm', header: 'Paid By KBM', width: 'auto' },
		{ field: 'stmtExpenseAmount', header: 'Stmt Expense Amount', width: 'auto' },
		{ field: 'stmtPaidByOwner', header: 'Stmt Paid By Owner', width: 'auto' },
		{ field: 'stmtPaidByKbm', header: 'Stmt Paid By KBM', width: 'auto' },
		{ field: 'discrepancyAmount', header: 'Discrepancy', width: 'auto' },
		{ field: 'expenseMatch', header: 'Match Status', width: 'auto' }
	];

	constructor(
		private formBuilder: UntypedFormBuilder,
		private ownerStmtService: OwnerStmtService,
		private reportService: ReportService,
		private alertService: AlertService,
		private messageService: MessageService,
		private dialog: MatDialog,
		private propertyService: PropertyService
	) { }

	ngOnInit(): void {
		this.loadShortTermRentals().then((success: boolean) => {
			this.loading = false;
			if(success) {
				this.formBuild();
			}
			else {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to load properties',
					life: 3000
				});
			}
		});
	}

	formBuild(): void {
		// Set default to current month/year
		const today = new Date();
		const currentMonthYear = new Date(today.getFullYear(), today.getMonth(), 1);
		
		this.form1 = this.formBuilder.group({
			shortTermRentalIds: [],
			monthYear: [currentMonthYear]
		});
    
    this.formSearch();
	}

	loadShortTermRentals(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.loading = true;
			this.propertyService.propertySummaryIndividualPropertyList().subscribe(
				data => {
					this.shortTermRentals = data.data.properties;
					resolve(true);
				},
				error => {
					console.log('Error loading properties: ' + JSON.stringify(error));
					this.alertService.error(error);
					resolve(false);
				}
			);
		});
	}

	formSearch(): void {
		const formValue = this.form1.value;
		
		if (!formValue.monthYear) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Validation Error',
				detail: 'Please select Month/Year',
				life: 3000
			});
			return;
		}

		this.loading = true;
		
		// Calculate start date (first day of selected month) and end date (last day of selected month)
		const selectedDate = moment(formValue.monthYear);
		const startDate = selectedDate.startOf('month').format('YYYY-MM-DD');
		const endDate = selectedDate.endOf('month').format('YYYY-MM-DD');

		this.ownerStmtService.reconcile(startDate, endDate).subscribe(
			(res: any) => {
				let data = res?.data?.ownerStmtExps || [];
				
				// Filter by selected properties if any
				if (formValue.shortTermRentalIds && formValue.shortTermRentalIds.length > 0) {
					const selectedIds = formValue.shortTermRentalIds.map((p: any) => p.shortTermRentalId);
					data = data.filter((item: any) => 
						selectedIds.includes(item.shortTermRentalId)
					);
				}

				// Transform data for display
				this.reconciliationData = data.map((item: any) => ({
					shortTermRentalId: item.shortTermRentalId,
					propertyCode: this.getPropertyCode(item.shortTermRentalId),
					startDate: item.startDate,
					endDate: item.endDate,
					expenseAmount: item.expenseAPI?.expenseAmount || 0,
					paidByOwner: item.expenseAPI?.paidByOwner || 0,
					paidByKbm: item.expenseAPI?.paidByKbm || 0,
					stmtExpenseAmount: item.stmtExpenseAPI?.expenseAmount || 0,
					stmtPaidByOwner: item.stmtExpenseAPI?.paidByOwner || 0,
					stmtPaidByKbm: item.stmtExpenseAPI?.paidByKbm || 0,
					discrepancyAmount: item.discrepancyAmount || 0,
					expenseMatch: item.expenseMatch || false,
					// Keep original data for reference
					originalData: item
				}));

				// Update property options from unique property codes in reconciliation data
				this.updatePropertyOptions();

				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `Loaded ${this.reconciliationData.length} reconciliation records`,
					life: 3000
				});
			},
			(err: any) => {
				console.log('Reconciliation error: ' + JSON.stringify(err));
				this.alertService.error(err);
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Failed to load reconciliation data',
					life: 3000
				});
			},
			() => {
				this.loading = false;
			}
		);
	}

	getPropertyCode(shortTermRentalId: number): string {
		const property = this.shortTermRentals.find((p: any) => p.shortTermRentalId === shortTermRentalId);
		return property ? property.propertyCode : `ID: ${shortTermRentalId}`;
	}

	getMatchStatusClass(match: boolean): string {
		return match ? 'match-success' : 'match-error';
	}

	getMatchStatusIcon(match: boolean): string {
		return match ? 'pi-check-circle' : 'pi-times-circle';
	}

	getMatchStatusText(match: boolean): string {
		return match ? 'Matched' : 'Mismatch';
	}

	getDiscrepancyClass(amount: number): string {
		return Math.abs(amount) < 0.01 ? 'match-success' : 'match-error';
	}

	reloadPage(): void {
		window.location.reload();
	}

	exportSelected($event: any): void {
		this.importExportComponent.exporData($event, this.reconciliationData, 'Owner Statement Reconciliation');
	}

	isValueMatch(expenseValue: number, stmtValue: number): boolean {
		return Math.abs(expenseValue - stmtValue) < 0.01;
	}

	updatePropertyOptions(): void {
		const uniquePropertyCodes = [...new Set(this.reconciliationData.map(item => item.propertyCode))];
		
		this.propertyOptions = uniquePropertyCodes
			.sort()
			.map(propertyCode => ({
				propertyCode: propertyCode
			}));
	}

	onPropertyFilterChange(event: any): void {
		const filterValue = event.value && event.value.length > 0 ? event.value : null;
		this.dt2.filter(filterValue, 'propertyCode', 'in');
	}


	openExpenseList(item: any, isStmt: boolean = false): void {
		const startDate = item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : null;
		const endDate = item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : null;
		
		// Build query parameters
		const queryParams: any = {};
		
		if (item.shortTermRentalId) {
			queryParams.shortTermRentalId = item.shortTermRentalId;
		}
		
		if (startDate) {
			queryParams.startDate = startDate;
		}
		
		if (endDate) {
			queryParams.endDate = endDate;
		}
		
		if (item.propertyCode) {
			queryParams.propertyCode = item.propertyCode;
		}

		if (isStmt) {
			queryParams.showToStmt = true;
		}

		// Build URL with query parameters
		const queryString = Object.keys(queryParams)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
			.join('&');
		
		const url = `/expense-list${queryString ? '?' + queryString : ''}`;
		
		// Open in new tab
		window.open(url, '_blank');
	}


	openStatementRez(item: any): void {
		const startDate = item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : null;
		const endDate = item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : null;

		// Build query parameters
		const queryParams: any = {};

		if (item.shortTermRentalId) {
			queryParams.shortTermRentalId = item.shortTermRentalId;
		}

		if (startDate) {
			queryParams.startDate = startDate;
		}

		if (endDate) {
			queryParams.endDate = endDate;
		}

		if (item.propertyCode) {
			queryParams.propertyCode = item.propertyCode;
		}
		// Build URL with query parameters
		const queryString = Object.keys(queryParams)
			.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
			.join('&');

		const url = `/owner-stmt-rez-list${queryString ? '?' + queryString : ''}`;

		// Open in new tab
		window.open(url, '_blank');
	}

	refreshReport(): void {
		this.formSearch();
	}
}
